import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Calendar, Target, Users, Image, DollarSign, Edit3 } from 'lucide-react';
import { AppButton } from '@/shared/components/ui/AppButton/AppButton';
import { AppCard } from '@/shared/components/ui/AppCard/AppCard';
import { useUIStore } from '@/shared/stores/ui-store';
import {
  reviewLaunchSchema,
  type ReviewLaunchFormValues,
} from '../../../schemas/review-launch.schema';
import {
  WIZARD_STEP_LABELS,
  OBJECTIVE_OPTIONS,
  CTA_OPTIONS,
  type WizardStep,
} from '../../../types/campaigns.types';
import { useCampaignWizardStore } from '../../../stores/campaign-wizard-store';
import {
  validateCreativeMedia,
  getTargetingCapabilities,
} from '../../../logic/platform-constraints';
import { resolveSlotImageUrls } from '../../../logic/creative-media';
import { CreativeType } from '@/shared/types/enums';
import styles from './ReviewLaunchStep.module.scss';

interface ReviewLaunchStepProps {
  onBack: () => void;
  onSaveDraft: () => void;
  onLaunch: () => void;
  onGoToStep: (step: WizardStep) => void;
  isSaving: boolean;
  isLaunching: boolean;
}

/**
 * Wizard Step 5: Review campaign summary and launch.
 */
export function ReviewLaunchStep({
  onBack,
  onSaveDraft,
  onLaunch,
  onGoToStep,
  isSaving,
  isLaunching,
}: ReviewLaunchStepProps) {
  const { formData } = useCampaignWizardStore();
  const addToast = useUIStore((s) => s.addToast);
  const isEventsManaged = formData.managementType === 'EVENTS_MANAGED';

  // Guard against launching a creative whose image upload never completed: the
  // preview can show while `generatedImages` (the persisted storage key) is
  // empty, which the backend rejects at launch for Google Display campaigns.
  const mediaErrors = useMemo(
    () => validateCreativeMedia(formData.creatives ?? [], formData.platform, formData.objective),
    [formData.creatives, formData.platform, formData.objective],
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ReviewLaunchFormValues>({
    resolver: zodResolver(reviewLaunchSchema),
    defaultValues: {
      agreedToTerms: formData.agreedToTerms ?? false,
    },
  });

  const selectedObjective = OBJECTIVE_OPTIONS.find((o) => o.value === formData.objective);
  const targetingCaps = getTargetingCapabilities(formData.platform);
  const firstCreative = formData.creatives?.[0];
  const selectedCta = CTA_OPTIONS.find((c) => c.value === firstCreative?.callToAction);
  const landscapePreviews = resolveSlotImageUrls(
    firstCreative?.generatedImages,
    firstCreative?.mediaPreviews,
  );
  const squarePreviews = resolveSlotImageUrls(
    firstCreative?.squareGeneratedImages,
    firstCreative?.squareMediaPreviews,
  );
  const isVideoCreative = firstCreative?.type === CreativeType.VIDEO;
  // Local blob URL for the picked video — valid within this session, which is
  // all the pre-launch review needs.
  const videoPreview = firstCreative?.videoPreview;
  const videoThumbnailPreview = firstCreative?.videoThumbnailPreview;
  const isCarouselCreative = firstCreative?.type === CreativeType.CAROUSEL;
  const carouselPreviews = (firstCreative?.carouselCards ?? [])
    .map((card) => card.imageUrl)
    .filter((url): url is string => Boolean(url));

  const formatBudget = (cents: number | undefined) => {
    if (!cents) return '--';
    return `$${(cents / 100).toFixed(2)}`;
  };

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return '--';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const onSubmit = () => {
    if (mediaErrors.length > 0) {
      addToast({ message: mediaErrors[0] ?? 'Add an image before launching.', variant: 'error' });
      return;
    }
    onLaunch();
  };

  return (
    <form
      className={styles.step}
      onSubmit={handleSubmit(onSubmit)}
      onKeyDown={(e) => {
        // Only the Launch button should submit the form; Enter in any
        // field must not trigger launch on its own.
        if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'BUTTON') {
          e.preventDefault();
        }
      }}
      noValidate
    >
      <div className={styles.header}>
        <h2 className={styles.title}>Review Your Campaign</h2>
        <p className={styles.subtitle}>
          Review all details below before launching. Click the edit icon on any section to make
          changes.
        </p>
      </div>

      <ReviewSection
        title={WIZARD_STEP_LABELS['objective-budget']}
        icon={<Target size={18} />}
        onEdit={() => onGoToStep('objective-budget')}
      >
        <div className={styles.reviewGrid}>
          <ReviewField label="Platform" value={formData.platform ?? '--'} />
          <ReviewField label="Ad Account" value={formData.adAccountId ?? '--'} />
          {formData.platform === 'META' && formData.pageName && (
            <ReviewField label="Publishing as" value={formData.pageName} />
          )}
          <ReviewField label="Campaign Name" value={formData.name ?? '--'} />
          <ReviewField label="Objective" value={selectedObjective?.label ?? '--'} />
          {formData.budgetStrategy === 'daily' ? (
            <ReviewField
              label="Daily budget"
              value={formatBudget(formData.dailyBudgetCents)}
              icon={<DollarSign size={14} />}
            />
          ) : (
            <ReviewField
              label="Total budget (lifetime)"
              value={formatBudget(formData.totalBudgetCents)}
              icon={<DollarSign size={14} />}
            />
          )}
          <ReviewField
            label="Start Date"
            value={formatDate(formData.startDate)}
            icon={<Calendar size={14} />}
          />
          <ReviewField
            label="End Date"
            value={formatDate(formData.endDate)}
            icon={<Calendar size={14} />}
          />
        </div>
      </ReviewSection>

      {/* Audience */}
      <ReviewSection
        title={WIZARD_STEP_LABELS['audience-targeting']}
        icon={<Users size={18} />}
        onEdit={() => onGoToStep('audience-targeting')}
      >
        <div className={styles.reviewGrid}>
          <ReviewField
            label="Locations"
            value={formData.targetingConfig?.locations?.map((l) => l.name).join(', ') || '--'}
          />
          <ReviewField
            label="Age Range"
            value={
              formData.targetingConfig
                ? `${formData.targetingConfig.ageMin} - ${formData.targetingConfig.ageMax}`
                : '--'
            }
          />
          <ReviewField
            label="Gender"
            value={formData.targetingConfig?.genders?.join(', ') ?? '--'}
          />
          <ReviewField
            label="Interests"
            value={formData.targetingConfig?.interests?.map((i) => i.name).join(', ') || 'None'}
          />
          {targetingCaps.behaviors && (
            <ReviewField
              label="Behaviors"
              value={formData.targetingConfig?.behaviors?.map((b) => b.name).join(', ') || 'None'}
            />
          )}
          {targetingCaps.keywords && (
            <ReviewField
              label="Keywords"
              value={formData.targetingConfig?.keywords?.join(', ') || 'None'}
            />
          )}
        </div>
      </ReviewSection>

      {/* Creative */}
      <ReviewSection
        title={WIZARD_STEP_LABELS['creative']}
        icon={<Image size={18} />}
        onEdit={() => onGoToStep('creative')}
      >
        <div className={styles.reviewGrid}>
          {firstCreative && (
            <>
              <ReviewField
                label="Images"
                value={`${
                  (firstCreative.generatedImages?.length ?? 0) +
                  (firstCreative.squareGeneratedImages?.length ?? 0)
                } image(s)`}
              />
              <ReviewField
                label="Headlines"
                value={`${(firstCreative.headlines ?? []).filter(Boolean).length} headline(s)`}
              />
              <ReviewField label="Headline" value={firstCreative.headlines?.[0] ?? '--'} />
              <ReviewField label="Description" value={firstCreative.descriptions?.[0] ?? '--'} />
              <ReviewField label="CTA" value={selectedCta?.label ?? '--'} />
            </>
          )}
          <ReviewField label="Destination URL" value={formData.destinationUrl ?? '--'} />
        </div>
        {isVideoCreative && (videoPreview || videoThumbnailPreview) && (
          <div className={styles.mediaThumbnails}>
            {videoPreview && (
              <video
                src={videoPreview}
                className={styles.videoThumbnail}
                controls
                preload="metadata"
              />
            )}
            {videoThumbnailPreview && (
              <img src={videoThumbnailPreview} alt="Video cover" className={styles.thumbnail} />
            )}
          </div>
        )}
        {isCarouselCreative && carouselPreviews.length > 0 && (
          <div className={styles.mediaThumbnails}>
            <div className={styles.thumbnailRow}>
              {carouselPreviews.map((preview, i) => (
                <img
                  key={preview}
                  src={preview}
                  alt={`Carousel card ${i + 1}`}
                  className={styles.thumbnail}
                />
              ))}
            </div>
          </div>
        )}
        {!isVideoCreative &&
          !isCarouselCreative &&
          (landscapePreviews.length > 0 || squarePreviews.length > 0) && (
            <div className={styles.mediaThumbnails}>
              {(
                [
                  { label: 'Landscape (1.91:1)', previews: landscapePreviews },
                  { label: 'Square (1:1)', previews: squarePreviews },
                ] as const
              )
                .filter((group) => group.previews.length > 0)
                .map((group) => (
                  <div key={group.label} className={styles.thumbnailGroup}>
                    {/* Only label the groups when the square slot is in play (Google Display) */}
                    {squarePreviews.length > 0 && (
                      <span className={styles.thumbnailGroupLabel}>{group.label}</span>
                    )}
                    <div className={styles.thumbnailRow}>
                      {group.previews.map((preview, i) => (
                        <img
                          key={preview}
                          src={preview}
                          alt={`${group.label} creative preview ${i + 1}`}
                          className={styles.thumbnail}
                        />
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          )}
        {mediaErrors.length > 0 && (
          <span className={styles.error} role="alert">
            {mediaErrors[0]}
          </span>
        )}
      </ReviewSection>

      {/* Terms Checkbox */}
      <AppCard className={styles.termsCard}>
        <label className={styles.termsLabel}>
          <input type="checkbox" className={styles.termsCheckbox} {...register('agreedToTerms')} />
          <span>
            I agree to the{' '}
            <a href="/terms" target="_blank" rel="noopener noreferrer" className={styles.termsLink}>
              advertising terms of service
            </a>{' '}
            and{' '}
            <a
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.termsLink}
            >
              privacy policy
            </a>
            . I confirm that my ad complies with platform advertising policies.
          </span>
        </label>
        {errors.agreedToTerms && (
          <span className={styles.error} role="alert">
            {errors.agreedToTerms.message}
          </span>
        )}
      </AppCard>

      <div className={styles.actions}>
        <AppButton type="button" variant="ghost" onClick={onSaveDraft} loading={isSaving}>
          Save as Draft
        </AppButton>
        <div className={styles.navActions}>
          <AppButton type="button" variant="secondary" onClick={onBack}>
            Back
          </AppButton>
          <AppButton type="submit" loading={isLaunching} disabled={mediaErrors.length > 0}>
            {isEventsManaged ? 'Submit for Review' : 'Launch Campaign'}
          </AppButton>
        </div>
      </div>
    </form>
  );
}

// -- Helper sub-components --

interface ReviewSectionProps {
  title: string;
  icon?: React.ReactNode;
  onEdit: () => void;
  children: React.ReactNode;
}

function ReviewSection({ title, icon, onEdit, children }: ReviewSectionProps) {
  return (
    <AppCard className={styles.reviewSection}>
      <div className={styles.reviewSectionHeader}>
        <div className={styles.reviewSectionTitle}>
          {icon}
          <h3>{title}</h3>
        </div>
        <button
          type="button"
          className={styles.editButton}
          onClick={onEdit}
          aria-label={`Edit ${title}`}
        >
          <Edit3 size={14} />
          Edit
        </button>
      </div>
      {children}
    </AppCard>
  );
}

interface ReviewFieldProps {
  label: string;
  value: string;
  icon?: React.ReactNode;
}

function ReviewField({ label, value, icon }: ReviewFieldProps) {
  return (
    <div className={styles.reviewField}>
      <span className={styles.reviewFieldLabel}>{label}</span>
      <span className={styles.reviewFieldValue}>
        {icon}
        {value}
      </span>
    </div>
  );
}
